package com.islamicmedia.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Stripe Checkout `success_url` landing page (`/billing-complete`). Checkout opens in a
 * separate tab via `window.open`, so after payment Stripe redirects here. The actual
 * credit grant happens server-side via the Stripe webhook; this page just reassures the
 * user and tells them to return to the app, where the tab-focus listener refreshes the
 * balance and auto-proceeds the pending search. See PRD_SUBSCRIPTION_BILLING.md (O5).
 *
 * NOTE (O5, mobile): for iOS/Android the real return needs this https URL to bridge back
 * into the app via a Universal Link / App Link — still to be wired.
 */
@Page
@Composable
fun BillingCompletePage() {
    var secondsLeft by remember { mutableStateOf(5) }
    // window.close() only succeeds for script-opened tabs (this one is, via window.open in
    // the checkout flow). If the browser blocks it, fall back to a "close this tab" prompt.
    var closeBlocked by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        while (secondsLeft > 0) {
            delay(1000)
            secondsLeft -= 1
        }
        window.close()
        delay(400)
        closeBlocked = true
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("min-height", "100vh")
            property("text-align", "center")
            padding(24.px)
            property("box-sizing", "border-box")
            property("font-family", "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif")
            property("background", "#0E7C7B")
            color(Color.white)
        }
    }) {
        Div(attrs = {
            style {
                width(72.px); height(72.px); borderRadius(50.percent)
                property("background", "rgba(255,255,255,0.15)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(40.px)
                marginBottom(20.px)
            }
        }) { Text("✓") }

        H1(attrs = { style { property("margin", "0"); fontSize(28.px); fontWeight(700) } }) {
            Text("Payment complete")
        }
        P(attrs = {
            style {
                fontSize(16.px); lineHeight(24.px.unsafeCast<CSSNumeric>())
                property("max-width", "420px"); property("opacity", "0.92")
                property("margin", "12px 0 0")
            }
        }) {
            Text("Your subscription is active. You can close this tab and return to Fitrah Filter. Your new searches are ready to go.")
        }

        P(attrs = {
            style {
                fontSize(14.px)
                property("opacity", "0.75")
                property("margin", "20px 0 0")
            }
        }) {
            Text(
                if (closeBlocked) "You can close this tab now."
                else "Closing this tab automatically in ${secondsLeft}s…"
            )
        }
    }
}
